import React, { FunctionComponent } from 'react';

import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import moment from 'moment';

import { useTheme } from 'styled-components';

import AnalyticsGraphLegend from './activity-graph-legend';
import { ActivityItem } from '@webapp/types/ui/analytics/activity-item';
import {
  // Area,
  Bar,
  ComposedChart,
  Tooltip,
  XAxis,
  YAxis,
  TooltipProps,
} from 'recharts';
import {
  ValueType,
  NameType,
} from 'recharts/types/component/DefaultTooltipContent';

import AppState from '@webapp/store/state';

import Box from '@components/layout/box';
import Flex from '@components/layout/flex';
import Typography from '@components/basics/typography';

import { TooltipWrapper } from './components';

import { numberFormatter, moneyFormatter } from './utils';

import ExampleData from './analytics-example-data.json';
import { GetArtistAnalytics } from '@webapp/store/artist-analytics/action-creators';
import { AnalyticsQueryTypes } from '@services/analytics/types/analytics-query-enum';

interface ActivityGraphBoxProps {
  className?: string;
  activities: ActivityItem[];
  isMobile: boolean;
  isLaptop: boolean;
  isExample: boolean;
  artistId?: string;
  getEngagementAnalytics: () => GetArtistAnalytics;
}

const groupByTimePeriod = (obj, timestamp, period) => {
  let objPeriod = {};
  const oneDay = 24 * 60 * 60 * 1000; // hours * minutes * seconds * milliseconds
  for (let i = 0; i < obj.length; i++) {
    let d: any = new Date(obj[i][timestamp]);
    switch (period) {
      case 'day':
        d = Math.floor(d.getTime() / oneDay);
        break;
      case 'week':
        d = Math.floor(d.getTime() / (oneDay * 7));
        break;
      case 'month':
        d = (d.getFullYear() - 1970) * 12 + d.getMonth();
        break;
      case 'year':
        d = d.getFullYear();
        break;
      default:
        break;
    }

    // define object key
    objPeriod[d] = objPeriod[d] || [];
    objPeriod[d].push(obj[i]);
  }
  return objPeriod;
};

const mapStateToProps = (state: AppState) => {
  let activities2: any = [];
  let objPeriodMonth;
  for (let i = parseInt(state.artistAnalytics.filters?.days); i > 0; i--) {
    let newSubDate = moment().subtract(i - 1, 'days');
    let streams: any = state.artistAnalytics.data?.streams.timeseries.filter(
      (val) =>
        moment(val?.date).utc().format('DD MM YYYY') ===
        newSubDate.utc().format('DD MM YYYY')
    );
    let listeners: any =
      state.artistAnalytics.data?.listeners.timeseries.filter(
        (val) =>
          moment(val?.date).utc().format('DD MM YYYY') ===
          newSubDate.utc().format('DD MM YYYY')
      );
    let downloads: any =
      state.artistAnalytics.data?.downloads.timeseries.filter(
        (val) =>
          moment(val?.date).utc().format('DD MM YYYY') ===
          newSubDate.utc().format('DD MM YYYY')
      );
    let saves: any = state.artistAnalytics.data?.saves.timeseries.filter(
      (val) =>
        moment(val?.date).utc().format('DD MM YYYY') ===
        newSubDate.utc().format('DD MM YYYY')
    );
    if (
      streams?.length > 0 ||
      listeners?.length > 0 ||
      downloads?.length > 0 ||
      saves?.length > 0
    ) {
      let st = 0;
      let li = 0;
      let dow = 0;
      let sa = 0;
      streams.forEach((val) => {
        st += val.value;
      });
      listeners.forEach((val) => {
        li += val.value;
      });
      downloads.forEach((val) => {
        dow += val.value;
      });
      saves.forEach((val) => {
        sa += val.value;
      });
      activities2.push({
        name: new Date(newSubDate.utc().toISOString()),
        streams: st,
        listeners: li,
        downloads: dow,
        saves: sa,
      });
    } else {
      activities2.push({
        name: new Date(newSubDate.utc().toISOString()),
        streams: 0,
        listeners: 0,
        downloads: 0,
        saves: 0,
      });
    }
  }
  if (state.artistAnalytics.filters?.range === 'allTime') {
    objPeriodMonth = groupByTimePeriod(activities2, 'name', 'year');
    activities2 = [];
    Object.keys(objPeriodMonth).forEach((key) => {
      let name = '';
      let streams = 0;
      let listeners = 0;
      let downloads = 0;
      let saves = 0;
      let timestamp = '';
      objPeriodMonth[key].forEach((val) => {
        name = val.name;
        streams += val.streams;
        listeners += val.listeners;
        downloads += val.downloads;
        saves += val.saves;
        timestamp = val.timestamp;
      });
      activities2.push({
        name: name,
        streams: streams,
        listeners: listeners,
        downloads: downloads,
        saves: saves,
        timestamp: timestamp,
      });
    });
  } else if (state.artistAnalytics.filters?.days === '365') {
    objPeriodMonth = groupByTimePeriod(activities2, 'name', 'month');
    activities2 = [];
    Object.keys(objPeriodMonth).forEach((key) => {
      let name = '';
      let streams = 0;
      let listeners = 0;
      let downloads = 0;
      let saves = 0;
      let timestamp = '';
      objPeriodMonth[key].forEach((val) => {
        name = val.name;
        streams += val.streams;
        listeners += val.listeners;
        downloads += val.downloads;
        saves += val.saves;
        timestamp = val.timestamp;
      });
      activities2.push({
        name: name,
        streams: streams,
        listeners: listeners,
        downloads: downloads,
        saves: saves,
        timestamp: timestamp,
      });
    });
  } else if (state.artistAnalytics.filters?.days === '90') {
    objPeriodMonth = groupByTimePeriod(activities2, 'name', 'week');
    activities2 = [];
    Object.keys(objPeriodMonth).forEach((key) => {
      let name = '';
      let streams = 0;
      let listeners = 0;
      let downloads = 0;
      let saves = 0;
      let timestamp = '';
      objPeriodMonth[key].forEach((val) => {
        name = val.name;
        streams += val.streams;
        listeners += val.listeners;
        downloads += val.downloads;
        saves += val.saves;
        timestamp = val.timestamp;
      });
      activities2.push({
        name: name,
        streams: streams,
        listeners: listeners,
        downloads: downloads,
        saves: saves,
        timestamp: timestamp,
      });
    });
  }

  return {
    activities: activities2,
    isMobile: state.ui.mobile,
    isLaptop: state.ui.laptop,
    artistId: state.artist.active?.id,
    isExample: state.artistAnalytics.isExample,
  };
};

const mapDispatchToProps = (dispatch: Dispatch) => ({
  getEngagementAnalytics: () =>
    dispatch(
      new GetArtistAnalytics(AnalyticsQueryTypes.ENGAGEMENT, undefined, true)
    ),
});

const CustomTooltip = ({
  active,
  payload,
  label,
}: TooltipProps<ValueType, NameType>) => {
  if (active && payload && payload.length) {
    return (
      <TooltipWrapper gap={10}>
        <Typography weight="700">
          {moment(label).utc().format('MMM DD‘YY')}
        </Typography>
        {payload.map((item) => (
          <Flex key={item.name} alignItems="baseline" gap={4}>
            <Typography weight="700" capitalize>
              {item.name}:
            </Typography>
            <Typography>
              {numberFormatter.format(Number(item.value))}
            </Typography>
          </Flex>
        ))}
      </TooltipWrapper>
    );
  }

  return null;
};

const ActivityGraphBox: FunctionComponent<ActivityGraphBoxProps> = ({
  className,
  activities,
  isMobile,
  isLaptop,
  isExample,
  artistId,
  getEngagementAnalytics,
}) => {
  const theme = useTheme();

  React.useEffect(() => {
    if (artistId) {
      getEngagementAnalytics();
    }
  }, [artistId, getEngagementAnalytics]);

  const getColors = (color: any) => {
    switch (color) {
      case 'yellow':
        return theme.colors.upload;
      case 'blue':
        return theme.colors.blueColor;
      case 'grey':
        return theme.colors.tertiary;
      case 'light-grey':
        return theme.colors.border;
      default:
        return theme.colors.primary;
    }
  };

  return (
    <Box className={className || ''} noBorderBottom>
      <Flex
        flexDirection={isLaptop ? 'column' : 'row'}
        justifyContent="space-between"
        alignItems={isLaptop ? 'flex-start' : 'center'}
        gap={40}
      >
        <div
          style={{
            width: isLaptop ? '100%' : isMobile ? 280 : 530,
            height: 230,
          }}
        >
          <ComposedChart
            width={isMobile ? 280 : 530}
            height={230}
            data={isExample ? ExampleData : activities}
          >
            <defs>
              <linearGradient id="areaColor" x1="0" y1="0" x2="0" y2="1">
                <stop
                  offset="0%"
                  stopColor={getColors('yellow')}
                  stopOpacity={1}
                />
                <stop
                  offset="100%"
                  stopColor={getColors('yellow')}
                  stopOpacity={0}
                />
              </linearGradient>
            </defs>
            <XAxis
              dataKey="name"
              height={12}
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: '10px', color: getColors('grey') }}
              tickFormatter={(value: string) =>
                moment(value).utc().format('DD')
              }
              interval="preserveStartEnd"
            />
            <YAxis
              width={32}
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: '10px', color: getColors('grey') }}
              tickFormatter={(value: number) => moneyFormatter.format(value)}
            />
            <Tooltip content={<CustomTooltip />} />
            {/* <Area
              type="linear"
              dataKey="listeners"
              fill="url(#areaColor)"
              stroke={getColors('yellow')}
            /> */}
            <Bar dataKey="streams" stackId="a" fill={getColors('blue')} />
            <Bar dataKey="saves" stackId="a" fill={getColors('grey')} />
            <Bar
              dataKey="downloads"
              stackId="a"
              fill={getColors('light-grey')}
            />
          </ComposedChart>
        </div>
        {!isMobile && <AnalyticsGraphLegend />}
      </Flex>
    </Box>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(ActivityGraphBox);
